import React, { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { DeviceUserService } from '../../services/device-user.service';
import { UserService } from '../../services/user.service';
import { InstitutionService } from '../../services/institution.service';
import { AuthService } from '../../services/auth.service';
import { DeviceUser } from '../../interfaces/deviceUser.interface';
import {
    Modal,
    Button,
    Box,
    Pagination,
    Autocomplete,
    TextField,
    ToggleButtonGroup,
    ToggleButton,
    Chip,
    Tab,
    Tabs,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    InputAdornment,
} from '@mui/material';
import { LinkOutlined, LinkOffOutlined } from '@mui/icons-material';
import { UserType } from '../../interfaces/userType.enum';
import { displayUsername } from '../../utils/userUtils';
import { useTranslation } from 'react-i18next';
import { useRoleLabel } from '../users/useRoleLabel';
import { useFreeTierNudge } from '../shared/FreeTierNudge';
import { usePlan } from '../../context/PlanContext';

interface SimpleUser {
    id: string;
    username: string;
    fullName?: string;
}

const DeviceUserList = () => {
    const [deviceUsers, setDeviceUsers] = useState<DeviceUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState<'all' | 'unmapped'>('all');
    const [pageInfo, setPageInfo] = useState({ currentPage: 0, totalPages: 0, totalElements: 0 });

    // Mapping modal state
    const [isMapModalOpen, setIsMapModalOpen] = useState(false);
    const [selectedDeviceUser, setSelectedDeviceUser] = useState<DeviceUser | null>(null);
    const [institutionUsers, setInstitutionUsers] = useState<SimpleUser[]>([]);
    const [selectedUserId, setSelectedUserId] = useState<SimpleUser | null>(null);
    const [userInput, setUserInput] = useState('');
    const [usersLoading, setUsersLoading] = useState(false);
    const [mapping, setMapping] = useState(false);
    const [unmappingId, setUnmappingId] = useState<string | null>(null);

    // Institution code for username prefix
    const [institutionCode, setInstitutionCode] = useState<string>('');

    // Create & Map tab state
    const [modalTab, setModalTab] = useState(0);
    const [newUsername, setNewUsername] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [newPhone, setNewPhone] = useState('');
    const [newUserType, setNewUserType] = useState<UserType>(UserType.User);
    const [creating, setCreating] = useState(false);

    // Table search state
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const toast = useToast();
    const { t } = useTranslation(['deviceUser', 'common']);
    const freeTier = useFreeTierNudge();
    const { refresh: refreshPlan } = usePlan();
    const roleLabel = useRoleLabel();

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login');
            return;
        }
        const institutionId = AuthService.getSelectedInstitution();
        if (institutionId) {
            InstitutionService.getInstitutionById(institutionId)
                .then((data: { code: string }) => setInstitutionCode(data.code))
                .catch(() => toast.showError(t('deviceUserList.toasts.loadInstitutionFailed')));
        }
    }, [isAuthenticated, navigate]);

    useEffect(() => {
        const handle = setTimeout(() => setDebouncedSearch(searchTerm), 300);
        return () => clearTimeout(handle);
    }, [searchTerm]);

    useEffect(() => {
        fetchDeviceUsers(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewMode, debouncedSearch]);

    useEffect(() => {
        if (!userInput.trim()) {
            setInstitutionUsers([]);
            return;
        }
        const handle = setTimeout(async () => {
            setUsersLoading(true);
            try {
                const response = await UserService.getInstitutionUsers(0, 20, undefined, userInput);
                setInstitutionUsers(response.content || []);
            } catch (err) {
                console.error('Error searching institution users:', err);
            } finally {
                setUsersLoading(false);
            }
        }, 300);
        return () => clearTimeout(handle);
    }, [userInput]);

    const fetchDeviceUsers = async (page: number = 0) => {
        try {
            setLoading(true);
            const response = viewMode === 'all'
                ? await DeviceUserService.getByInstitution(page, 10, debouncedSearch)
                : await DeviceUserService.getUnmapped(page, 10, debouncedSearch);
            setDeviceUsers(response.content || []);
            setPageInfo({
                currentPage: response.number,
                totalPages: response.totalPages,
                totalElements: response.totalElements
            });
        } catch (err: any) {
            toast.showApiError(err, t('deviceUserList.toasts.loadFailed'));
        } finally {
            setLoading(false);
        }
    };

    const resetCreateForm = () => {
        setNewUsername('');
        setNewPassword('');
        setNewPhone('');
        setNewUserType(UserType.User);
    };

    const handleOpenMapModal = (deviceUser: DeviceUser) => {
        setSelectedDeviceUser(deviceUser);
        setSelectedUserId(null);
        setUserInput('');
        setInstitutionUsers([]);
        setModalTab(0);
        resetCreateForm();
        setNewUsername((deviceUser.username || '').replace(/\s+/g, '_'));
        setIsMapModalOpen(true);
    };

    const handleCloseMapModal = () => {
        setIsMapModalOpen(false);
        setSelectedDeviceUser(null);
        setSelectedUserId(null);
        setUserInput('');
        setInstitutionUsers([]);
        setModalTab(0);
        resetCreateForm();
    };

    const handleMapUser = async () => {
        if (!selectedDeviceUser || !selectedUserId) return;

        try {
            setMapping(true);
            await DeviceUserService.mapToUser(selectedDeviceUser.id, selectedUserId.id);
            toast.showSuccess(t('deviceUserList.toasts.mapped'));
            handleCloseMapModal();
            await fetchDeviceUsers(pageInfo.currentPage);
        } catch (err: any) {
            toast.showApiError(err, t('deviceUserList.toasts.mapFailed'));
        } finally {
            setMapping(false);
        }
    };

    const handleCreateAndMap = async () => {
        if (!selectedDeviceUser || !newUsername || !newPassword) return;

        try {
            setCreating(true);
            const newUser = await UserService.createUser({
                username: institutionCode ? `${institutionCode}_${newUsername}` : newUsername,
                password: newPassword,
                phone: newPhone,
                type: newUserType,
            });
            refreshPlan();
            await DeviceUserService.mapToUser(selectedDeviceUser.id, newUser.id);
            toast.showSuccess(t('deviceUserList.toasts.createdAndMapped'));
            handleCloseMapModal();
            await fetchDeviceUsers(pageInfo.currentPage);
        } catch (err: any) {
            if (!freeTier.handle(err)) toast.showApiError(err, t('deviceUserList.toasts.createAndMapFailed'));
        } finally {
            setCreating(false);
        }
    };

    const handleUnmapUser = async (deviceUser: DeviceUser) => {
        const mappedLabel = deviceUser.mappedUsername ? displayUsername(deviceUser.mappedUsername) : t('deviceUserList.theMappedUser');
        if (!window.confirm(t('deviceUserList.unmapConfirm', {
            deviceUser: deviceUser.username || deviceUser.deviceUserId,
            mappedUser: mappedLabel,
        }))) {
            return;
        }
        try {
            setUnmappingId(deviceUser.id);
            await DeviceUserService.unmapUser(deviceUser.id);
            toast.showSuccess(t('deviceUserList.toasts.unmapped'));
            await fetchDeviceUsers(pageInfo.currentPage);
        } catch (err: any) {
            toast.showApiError(err, t('deviceUserList.toasts.unmapFailed'));
        } finally {
            setUnmappingId(null);
        }
    };

    const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
        fetchDeviceUsers(page - 1);
    };

    const handleViewModeChange = (_event: React.MouseEvent<HTMLElement>, newMode: 'all' | 'unmapped' | null) => {
        if (newMode !== null) {
            setViewMode(newMode);
        }
    };

    const formatDate = (dateStr: string | null) => {
        if (!dateStr) return '-';
        return format(parseISO(dateStr), 'dd-MM-yyyy HH:mm');
    };

    if (loading && deviceUsers.length === 0) return <div>{t('deviceUserList.loading')}</div>;

    return (
        <div style={{ padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2>{t('deviceUserList.title')}</h2>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField
                        label={t('deviceUserList.searchByUsername')}
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ width: 260 }}
                    />
                    <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={handleViewModeChange}
                        size="small"
                    >
                        <ToggleButton value="all">{t('deviceUserList.all')}</ToggleButton>
                        <ToggleButton value="unmapped">{t('deviceUserList.unmapped')}</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            </div>
            <div style={{ marginBottom: '20px' }}>
                {t('deviceUserList.total', { total: pageInfo.totalElements })}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr>
                        <th style={tableHeaderStyle}>{t('deviceUserList.columns.deviceId')}</th>
                        <th style={tableHeaderStyle}>{t('deviceUserList.columns.deviceUserId')}</th>
                        <th style={tableHeaderStyle}>{t('deviceUserList.columns.username')}</th>
                        <th style={tableHeaderStyle}>{t('deviceUserList.columns.userPin')}</th>
                        <th style={tableHeaderStyle}>{t('deviceUserList.columns.department')}</th>
                        <th style={tableHeaderStyle}>{t('deviceUserList.columns.fingerprints')}</th>
                        <th style={tableHeaderStyle}>{t('deviceUserList.columns.enrolledAt')}</th>
                        <th style={tableHeaderStyle}>{t('deviceUserList.columns.mappedUser')}</th>
                        <th style={tableHeaderStyle}>{t('deviceUserList.columns.lastSynced')}</th>
                        <th style={tableHeaderStyle}>{t('deviceUserList.columns.actions')}</th>
                    </tr>
                </thead>
                <tbody>
                    {deviceUsers.map(du => (
                        <tr key={du.id}>
                            <td style={tableCellStyle}>{du.deviceId}</td>
                            <td style={tableCellStyle}>{du.deviceUserId}</td>
                            <td style={tableCellStyle}>{du.username || '-'}</td>
                            <td style={tableCellStyle}>{du.userPin || '-'}</td>
                            <td style={tableCellStyle}>{du.departmentName || '-'}</td>
                            <td style={tableCellStyle}>{du.fingerprintCount}</td>
                            <td style={tableCellStyle}>{formatDate(du.enrolledAt)}</td>
                            <td style={tableCellStyle}>
                                {du.mappedUserId ? (
                                    <Chip
                                        label={du.mappedUsername ? displayUsername(du.mappedUsername) : t('deviceUserList.mapped')}
                                        color="success"
                                        size="small"
                                    />
                                ) : (
                                    <Chip label={t('deviceUserList.unmapped')} color="warning" size="small" variant="outlined" />
                                )}
                            </td>
                            <td style={tableCellStyle}>{formatDate(du.lastSyncedAt)}</td>
                            <td style={tableCellStyle}>
                                {!du.mappedUserId ? (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<LinkOutlined />}
                                        onClick={() => handleOpenMapModal(du)}
                                    >
                                        {t('deviceUserList.mapUser')}
                                    </Button>
                                ) : (
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<LinkOutlined />}
                                            onClick={() => handleOpenMapModal(du)}
                                        >
                                            {t('deviceUserList.remap')}
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="warning"
                                            startIcon={<LinkOffOutlined />}
                                            disabled={unmappingId === du.id}
                                            onClick={() => handleUnmapUser(du)}
                                        >
                                            {unmappingId === du.id ? t('deviceUserList.unmapping') : t('deviceUserList.unmap')}
                                        </Button>
                                    </Box>
                                )}
                            </td>
                        </tr>
                    ))}
                    {deviceUsers.length === 0 && (
                        <tr>
                            <td style={tableCellStyle} colSpan={10}>
                                {t('deviceUserList.empty')}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>

            {pageInfo.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                        count={pageInfo.totalPages}
                        page={pageInfo.currentPage + 1}
                        onChange={handlePageChange}
                        color="primary"
                        showFirstButton
                        showLastButton
                    />
                </Box>
            )}

            {/* Map User Modal */}
            <Modal open={isMapModalOpen} onClose={handleCloseMapModal}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 450,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                }}>
                    <h3>{t('deviceUserList.mapModal.title')}</h3>
                    {selectedDeviceUser && (
                        <Box sx={{ mb: 2, color: 'text.secondary', fontSize: '0.9rem' }}>
                            {t('deviceUserList.mapModal.deviceInfo', { deviceId: selectedDeviceUser.deviceId, deviceUserId: selectedDeviceUser.deviceUserId })}
                            {selectedDeviceUser.username && ` | ${t('deviceUserList.mapModal.usernameInfo', { username: selectedDeviceUser.username })}`}
                            {selectedDeviceUser.userPin && ` | ${t('deviceUserList.mapModal.pinInfo', { pin: selectedDeviceUser.userPin })}`}
                        </Box>
                    )}
                    <Tabs value={modalTab} onChange={(_e, v) => { setModalTab(v); resetCreateForm(); if (v === 1 && selectedDeviceUser) setNewUsername((selectedDeviceUser.username || '').replace(/\s+/g, '_')); setSelectedUserId(null); setUserInput(''); setInstitutionUsers([]); }} sx={{ mb: 2 }}>
                        <Tab label={t('deviceUserList.mapModal.selectExisting')} />
                        <Tab label={t('deviceUserList.mapModal.createAndMap')} />
                    </Tabs>

                    {modalTab === 0 && (
                        <Autocomplete
                            options={institutionUsers}
                            getOptionLabel={(option) => option.fullName ? `${option.fullName} (${displayUsername(option.username)})` : displayUsername(option.username)}
                            value={selectedUserId}
                            onChange={(_event, newValue) => setSelectedUserId(newValue)}
                            onInputChange={(_event, value, reason) => {
                                if (reason === 'input') {
                                    setUserInput(value);
                                } else if (reason === 'clear') {
                                    setUserInput('');
                                }
                            }}
                            filterOptions={(x) => x}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            loading={usersLoading}
                            noOptionsText={userInput ? t('deviceUserList.mapModal.noUsers') : t('deviceUserList.mapModal.typeToSearch')}
                            renderInput={(params) => (
                                <TextField {...params} label={t('deviceUserList.mapModal.selectUser')} fullWidth />
                            )}
                        />
                    )}

                    {modalTab === 1 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                                label={t('deviceUserList.mapModal.username')}
                                value={newUsername}
                                onChange={(e) => setNewUsername(e.target.value.replace(/\s/g, '_'))}
                                fullWidth
                                required
                                InputProps={institutionCode ? {
                                    startAdornment: (
                                        <InputAdornment position="start">{institutionCode}_</InputAdornment>
                                    )
                                } : {}}
                            />
                            <TextField
                                label={t('deviceUserList.mapModal.password')}
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                fullWidth
                                required
                            />
                            <TextField
                                label={t('deviceUserList.mapModal.phone')}
                                value={newPhone}
                                onChange={(e) => setNewPhone(e.target.value)}
                                fullWidth
                            />
                            <FormControl fullWidth>
                                <InputLabel>{t('deviceUserList.mapModal.userType')}</InputLabel>
                                <Select
                                    value={newUserType}
                                    label={t('deviceUserList.mapModal.userType')}
                                    onChange={(e) => setNewUserType(e.target.value as UserType)}
                                >
                                    {Object.values(UserType)
                                        .filter(type => type !== UserType.SystemAdmin)
                                        .map(type => (
                                            <MenuItem key={type} value={type}>{roleLabel(type)}</MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        </Box>
                    )}

                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button onClick={handleCloseMapModal} variant="outlined">
                            {t('common:actions.cancel')}
                        </Button>
                        {modalTab === 0 ? (
                            <Button
                                onClick={handleMapUser}
                                variant="contained"
                                color="primary"
                                disabled={!selectedUserId || mapping}
                            >
                                {mapping ? t('deviceUserList.mapping') : t('deviceUserList.mapUser')}
                            </Button>
                        ) : (
                            <Button
                                onClick={handleCreateAndMap}
                                variant="contained"
                                color="primary"
                                disabled={!newUsername || !newPassword || creating}
                                startIcon={creating ? <CircularProgress size={16} /> : undefined}
                            >
                                {creating ? t('deviceUserList.creating') : t('deviceUserList.mapModal.createAndMap')}
                            </Button>
                        )}
                    </Box>
                </Box>
            </Modal>
            {freeTier.element}
        </div>
    );
};

const tableHeaderStyle = {
    backgroundColor: '#f4f4f4',
    padding: '12px',
    textAlign: 'left' as const,
    borderBottom: '2px solid #ddd'
};

const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #ddd'
};

export default DeviceUserList;
